/*
 * Copyright 1999-2024 Jagex Ltd.
 */
import { ParsedUrlQuery } from 'querystring';
import { action, observable, computed } from 'mobx';
import { StatusCode } from '@common/constants';
import { ERROR_CODE_MESSAGES } from '@common/messages';
import { Routes } from '@common/routes';
import { DEFAULT_LOCALE_KEY } from '@i18n/config';
import { auth } from '@stores/auth/decorators';
import { loader } from '@stores/loading/decorators';
import { Loading } from '@stores/loading/loading';
import { notify } from '@stores/notifications/decorators';
import { Notifications } from '@stores/notifications/notifications';
import { checkErrorStatusCode, extractValidationErrorResponse } from '@utils/network';
import { Request } from './requests';
import { AuthStatus, LoginInfo, ValidateRedirectUrlRequestData } from './types';

export class Auth {
  @observable authStatus: AuthStatus = AuthStatus.UNKNOWN;

  constructor(private loading: Loading, private notifications: Notifications) {
    this.loading.registerLoader('auth-check-status', true);
  }

  @action.bound
  setAuthState(state: AuthStatus): void {
    this.authStatus = state;
  }

  @computed
  get isAuthenticated(): boolean {
    return this.authStatus === AuthStatus.AUTHORIZED;
  }

  @loader('auth-check-status')
  @notify()
  async checkAuthStatus(): Promise<void> {
    try {
      await Request.checkAuthStatus();
      this.setAuthState(AuthStatus.AUTHORIZED);
    } catch (err) {
      const isUnauthorized = checkErrorStatusCode(StatusCode.Unauthorized, err);

      if (isUnauthorized) {
        this.setAuthState(AuthStatus.UNAUTHORIZED);
      } else {
        return Promise.reject(err);
      }
    }
  }

  @notify()
  async getAuthTokens(queryParams: ParsedUrlQuery): Promise<void> {
    await Request.getOAuthTokens(queryParams);
  }

  @notify()
  async getLoginLink(locale?: string): Promise<LoginInfo> {
    return Request.getLoginLink({ params: { locale } });
  }

  @notify(null, ERROR_CODE_MESSAGES.DEFAULT_LOGOUT_ERROR_MESSAGE)
  async logout(lang = DEFAULT_LOCALE_KEY): Promise<void> {
    try {
      const { redirectURL } = await Request.logout();

      if (redirectURL) {
        window.location.assign(redirectURL);
      } else {
        window.location.assign(Routes.Account.url({ lang }));
      }
    } catch (e) {
      window.location.assign(Routes.Account.url({ lang }));
    }
  }

  @auth
  async validateRedirectUrl(body: ValidateRedirectUrlRequestData): Promise<void> {
    return Request.validateRedirectUrl(body).catch(e =>
      extractValidationErrorResponse(e, this.notifications.enqueueDefaultError)
    );
  }
}
